import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  Stack,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../AuthContext';

function SecurityManager() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // State
  const [addIPDialogOpen, setAddIPDialogOpen] = useState(false);
  const [newIPAddress, setNewIPAddress] = useState('');
  const [newIPDescription, setNewIPDescription] = useState('');
  const [alerts, setAlerts] = useState([]);

  const showAlert = (message, severity = 'info') => {
    const alert = { id: Date.now(), message, severity };
    setAlerts(prev => [...prev, alert]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    }, 5000);
  };

  // Queries
  const { data: ipWhitelistData, isLoading, refetch } = useQuery({
    queryKey: ['ipWhitelist'],
    queryFn: async () => {
      const response = await api.get('/security/ip-whitelist');
      return response.data;
    }
  });

  const { data: currentIPData } = useQuery({
    queryKey: ['currentIP'],
    queryFn: async () => {
      const response = await api.get('/security/current-ip');
      return response.data;
    }
  });

  // Mutations
  const addIPMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/security/ip-whitelist', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ipWhitelist']);
      setAddIPDialogOpen(false);
      setNewIPAddress('');
      setNewIPDescription('');
      showAlert('IP address added to whitelist successfully!', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to add IP address', 'error');
    }
  });

  const removeIPMutation = useMutation({
    mutationFn: async (ipAddress) => {
      const response = await api.delete(`/security/ip-whitelist/${encodeURIComponent(ipAddress)}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ipWhitelist']);
      showAlert('IP address removed from whitelist successfully!', 'success');
    },
    onError: (error) => {
      showAlert(error.response?.data?.error || 'Failed to remove IP address', 'error');
    }
  });

  const handleAddIP = () => {
    if (!newIPAddress.trim()) {
      showAlert('IP address is required', 'error');
      return;
    }
    
    addIPMutation.mutate({
      ipAddress: newIPAddress.trim(),
      description: newIPDescription.trim()
    });
  };

  const handleRemoveIP = (ipAddress) => {
    if (window.confirm(`Are you sure you want to remove ${ipAddress} from the whitelist?`)) {
      removeIPMutation.mutate(ipAddress);
    }
  };

  const addCurrentIP = () => {
    if (currentIPData?.ip) {
      setNewIPAddress(currentIPData.ip);
      setNewIPDescription('My current IP address');
      setAddIPDialogOpen(true);
    }
  };

  if (!user || user.role !== 'super_admin') {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography variant="h6">Access denied. Super admin privileges required.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" component="div" flexGrow={1}>
          Security Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refetch()}
        >
          Refresh
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddIPDialogOpen(true)}
        >
          Add IP Address
        </Button>
      </Stack>

      {/* Alerts */}
      {alerts.map(alert => (
        <Alert key={alert.id} severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      ))}

      {/* Current IP Info */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <SecurityIcon color="primary" />
            <Box flexGrow={1}>
              <Typography variant="h6">Your Current IP Address</Typography>
              <Typography variant="body2" color="text.secondary" fontFamily="monospace">
                {currentIPData?.ip || 'Loading...'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<MyLocationIcon />}
              onClick={addCurrentIP}
              disabled={!currentIPData?.ip}
            >
              Add Current IP
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* IP Whitelist Status */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          IP Whitelist Status
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip 
            label={ipWhitelistData?.isProduction ? 'Production Mode' : 'Development Mode'} 
            color={ipWhitelistData?.isProduction ? 'error' : 'success'}
          />
          <Typography variant="body2" color="text.secondary">
            IP filtering is ACTIVE - only whitelisted IPs can access admin features
          </Typography>
        </Stack>
      </Paper>

      {/* IP Whitelist Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Whitelisted IP Addresses ({ipWhitelistData?.ipWhitelist?.length || 0})
        </Typography>
        
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : ipWhitelistData?.ipWhitelist?.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="text.secondary">
              No IP addresses whitelisted. Add your IP to access the system.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>IP Address</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ipWhitelistData?.ipWhitelist?.map((ip) => (
                  <TableRow key={ip}>
                    <TableCell fontFamily="monospace">{ip}</TableCell>
                    <TableCell>
                      <Chip 
                        label={ip.includes('/') ? 'CIDR Range' : 'Single IP'} 
                        size="small"
                        color={ip.includes('/') ? 'secondary' : 'primary'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={ip === currentIPData?.ip ? 'Your IP' : 'Active'} 
                        size="small"
                        color={ip === currentIPData?.ip ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveIP(ip)}
                        disabled={removeIPMutation.isLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add IP Dialog */}
      <Dialog open={addIPDialogOpen} onClose={() => setAddIPDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add IP Address to Whitelist</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="IP Address or CIDR Range"
              value={newIPAddress}
              onChange={(e) => setNewIPAddress(e.target.value)}
              placeholder="192.168.1.100 or 10.0.0.0/8"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description (optional)"
              value={newIPDescription}
              onChange={(e) => setNewIPDescription(e.target.value)}
              placeholder="Office network, Home IP, etc."
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddIPDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddIP} 
            variant="contained"
            disabled={addIPMutation.isLoading || !newIPAddress.trim()}
          >
            {addIPMutation.isLoading ? <CircularProgress size={20} /> : 'Add IP Address'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SecurityManager;
